import { makeAutoObservable } from 'mobx'
import {
  EnumDropdownItemVariant,
  type IAvatarInfo,
  type IDropdownItem,
  showToast,
  toastStore,
} from 'shared/ui'
import { AuthApi } from 'entities/Auth/api'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { TwoFaModalStore } from 'entities/Auth/ui/TwoFaModal'

export class MultiOrganizationsDropdownStore {
  private _searchValue = ''
  private readonly _twoFaModalStore: TwoFaModalStore

  constructor(private _entities: IEntitiesStore) {
    this._twoFaModalStore = new TwoFaModalStore(this._entities.authStore)

    makeAutoObservable(this)
  }

  get searchValue() {
    return this._searchValue
  }

  setSearchValue = (key: string) => {
    this._searchValue = key
  }

  filterItems = (items: IDropdownItem[]): IDropdownItem[] => {
    const key = this._searchValue.toLocaleLowerCase().replaceAll(/\s/g, '')
    const result = items?.filter((item) =>
      String(item.label).toLocaleLowerCase().replaceAll(/\s/g, '').includes(key)
    )

    if (result?.length) {
      return result
    }

    return []
  }

  changeActiveOrganization = async (
    item: IDropdownItem,
    onClose: () => void,
    onCloseParent?: () => void
  ) => {
    try {
      const params = {
        organization_id: Number(item.id),
      }

      const { data } = await AuthApi.switch(params)

      if (data?.data?.is_2fa) {
        onClose()
        onCloseParent?.()

        this._twoFaModalStore.openTwoFaModal({
          settings: data.data.settings,
          owner_email_settings: data.data.owner_email_settings,
          onSuccess: () => {
            window.location.href = '/'
          },
        })
      } else if (data.switched) {
        onClose()
        window.location.href = '/'
      }

      if (data.error) {
        showToast({
          type: 'error',
          title: data.error,
        })
      }
    } catch (e) {
      console.log(e)

      toastStore.add({
        type: 'error',
        title: 'Something went wrong, try again!',
      })
    }
  }

  get items() {
    const data = this._entities.multiOrganizationStore.multiOrganizations.map((organization) => {
      const itemInfo: IDropdownItem = {
        id: `${organization.id}`,
        label: organization.name,
        avatarProps: {
          info: {
            image: organization.photo_url || '',
            color: '',
            firstName: organization.name,
          },
          size: 16,
          iconProps: {
            fontSize: 12,
          },
        },
        activeValue: this.activeOrganisation?.id === organization.id,
        variant: EnumDropdownItemVariant.CheckedRight,
      }

      return itemInfo
    })

    if (this._searchValue) {
      return this.filterItems(data)
    }

    return data
  }

  get hasMultiOrganisations() {
    return Boolean(this._entities.multiOrganizationStore.multiOrganizations?.length)
  }

  get multiOrganisationsCount() {
    return this._entities.multiOrganizationStore.multiOrganizations?.length || 0
  }

  get activeOrganisation() {
    return this._entities.multiOrganizationStore.multiOrganizations?.filter(
      (item) => item.id === this._entities.organizationStore.id
    )[0]
  }

  get activeOrganisationName() {
    return this.activeOrganisation?.name || ''
  }

  get activeOrganizationAvatarInfo(): IAvatarInfo {
    return {
      image: this.activeOrganisation?.photo_url || '',
      color: '',
      firstName: this.activeOrganisationName,
    }
  }
}
